import {
  Habit,
  QueryFilterOptionKeys,
  RepeatingTask,
  TaskFilterOptions,
  TaskPriorityOptions,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { cleanDeep } from './clean-deep';
import { getDueTaskPlanning, getTaskPlanning } from './get-task-planning';
import { isNumber } from './is-number';
import { getScheduleByDate } from './schedules';

export type ProcessScheduleTasksOptions = {
  weekStartsOn: WeekDays;
  today: Timestamp;
};

export const processScheduleTasks = (
  tasks: Habit[] | RepeatingTask[],
  filters: TaskFilterOptions | TaskFilterOptions[],
  { weekStartsOn, today }: ProcessScheduleTasksOptions,
) => {
  const filtersArray = Array.isArray(filters) ? filters : [filters];

  // Optimization: Clean filters once
  const processedFiltersArray = filtersArray.map((f) => cleanDeep(f));

  // If any filter set is empty, it means "no restrictions" for that set, so it matches everything.
  const hasEmptyFilter = processedFiltersArray.some(
    (f) => !Object.values(f).length,
  );

  if (hasEmptyFilter) {
    return tasks;
  }

  // process every filter
  return tasks.filter((task) =>
    // Check if task matches ANY of the filter sets
    processedFiltersArray.some((processedFilters, index) => {
      if (
        (processedFilters.deleted && !task.deletedAt) ||
        (!processedFilters.deleted && !!task.deletedAt)
      ) {
        return false;
      }
      const originalFilter = filtersArray[index];
      // filtering on ids without any ids present means no tasks
      if (originalFilter.ids && !originalFilter.ids.length) {
        return false;
      }

      if (
        processedFilters.deadlineDeadlineStartDate ||
        processedFilters.deadlineDeadlineEndDate ||
        processedFilters.hasDeadlineDeadline ||
        processedFilters.noDeadline
      ) {
        return false;
      }

      if (
        processedFilters.priorities?.length &&
        !(
          !task.priority &&
          processedFilters.priorities.includes(TaskPriorityOptions.None)
        ) &&
        !processedFilters.priorities.includes(task.priority!)
      ) {
        return false;
      }

      if (
        processedFilters.ids?.length &&
        !processedFilters.ids.includes(task.id)
      ) {
        return false;
      }

      if (processedFilters.noLifeArea && task.lifeAreaId) {
        return false;
      }

      if (processedFilters.noGoal && task.goalId) {
        return false;
      }

      if (
        processedFilters.archivedGoalIds?.length &&
        task.goalId &&
        processedFilters.archivedGoalIds.includes(task.goalId)
      ) {
        return false;
      }

      const linkedItemIds =
        processedFilters.lifeAreas?.length || processedFilters.goals?.length
          ? [
              ...(processedFilters.lifeAreas ?? []),
              ...(processedFilters.goals ?? []),
            ]
          : undefined;

      if (
        linkedItemIds &&
        !(
          (task.lifeAreaId && linkedItemIds.includes(task.lifeAreaId)) ||
          (task.goalId && linkedItemIds.includes(task.goalId))
        )
      ) {
        return false;
      }

      const shouldCheckPlanning =
        processedFilters[QueryFilterOptionKeys.DeadlineStartDate] ||
        processedFilters[QueryFilterOptionKeys.DeadlineEndDate];

      if (!shouldCheckPlanning && isNumber(processedFilters.maxDuration)) {
        const schedule = getScheduleByDate(task.schedules, {
          date: today,
          today,
        });
        return (
          isNumber(schedule?.timeslot?.duration) &&
          schedule!.timeslot!.duration <= processedFilters.maxDuration
        );
      }

      if (!shouldCheckPlanning && isNumber(processedFilters.minDuration)) {
        const schedule = getScheduleByDate(task.schedules, {
          date: today,
          today,
        });
        return (
          isNumber(schedule?.timeslot?.duration) &&
          schedule!.timeslot!.duration >= processedFilters.minDuration
        );
      }

      if (!shouldCheckPlanning) {
        return true;
      }

      const planning =
        processedFilters[QueryFilterOptionKeys.DeadlineStartDate] &&
        processedFilters[QueryFilterOptionKeys.DeadlineEndDate]
          ? getTaskPlanning(task, {
              startDate: processedFilters[
                QueryFilterOptionKeys.DeadlineStartDate
              ] as Timestamp,
              endDate: processedFilters[
                QueryFilterOptionKeys.DeadlineEndDate
              ] as Timestamp,
              today,
              weekStartsOn,
              uncompletedOnly: true,
            })
          : getDueTaskPlanning(task, {
              date: (processedFilters[
                QueryFilterOptionKeys.DeadlineStartDate
              ] ??
                processedFilters[QueryFilterOptionKeys.DeadlineEndDate] ??
                today) as Timestamp,
              uncompletedOnly: true,
              weekStartsOn,
              today,
            });

      if (!planning.length) {
        return false;
      }

      const plannedOccurrence = planning[0];

      if (
        processedFilters[QueryFilterOptionKeys.DeadlineStartDate] &&
        plannedOccurrence.date <
          processedFilters[QueryFilterOptionKeys.DeadlineStartDate]
      ) {
        return false;
      }
      if (
        processedFilters[QueryFilterOptionKeys.DeadlineEndDate] &&
        plannedOccurrence.date >
          processedFilters[QueryFilterOptionKeys.DeadlineEndDate]
      ) {
        return false;
      }

      if (
        isNumber(processedFilters.maxDuration) &&
        (!plannedOccurrence.timeslot?.duration ||
          plannedOccurrence.timeslot.duration > processedFilters.maxDuration)
      ) {
        return false;
      }

      if (
        isNumber(processedFilters.minDuration) &&
        (!plannedOccurrence.timeslot?.duration ||
          plannedOccurrence.timeslot.duration < processedFilters.minDuration)
      ) {
        return false;
      }

      if (task.completedAt) {
        if (
          processedFilters[QueryFilterOptionKeys.CompletedStartDate] &&
          task.completedAt <
            processedFilters[QueryFilterOptionKeys.CompletedStartDate]
        ) {
          return false;
        }
        if (
          processedFilters[QueryFilterOptionKeys.CompletedEndDate] &&
          task.completedAt >
            processedFilters[QueryFilterOptionKeys.CompletedEndDate]
        ) {
          return false;
        }
      }

      return true;
    }),
  );
};
